import { useState, useRef, useEffect } from "react";
import type { ChatSession, ConnectionStatus } from "@/types";
import { GlassOrb } from "./GlassOrb";
import { SidebarToggleIcon } from "./SidebarToggleIcon";
import { ProductMark } from "@fwf/fwfa-design-system/react";

/** Soft atmospheric overlays that sit behind the frosted-glass sidebar surface. */
function SidebarBlooms() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-b from-[#001018] to-[#00202e]" />
      <div className="absolute top-0 left-0 w-full h-[70%] bg-[radial-gradient(ellipse_90%_60%_at_15%_8%,rgba(0,120,135,0.35)_0%,transparent_55%)]" />
      <div className="absolute bottom-0 left-0 w-full h-[50%] bg-[radial-gradient(ellipse_80%_50%_at_50%_95%,rgba(0,55,75,0.15)_0%,transparent_60%)]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
    </div>
  );
}

type UserProfile = {
  name: string;
  email: string;
};

interface Props {
  sessions: ChatSession[];
  activeSessionId: string | null;
  workingDirectory: string | null;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onNewChat: (folder?: string) => void;
  onNewProject: () => void;
  onSettingsClick: () => void;
  workspaceTarget?: {
    status: "connected" | "none";
    terminalStatus?: "disconnected" | "connecting" | "connected" | "error";
    agentStatus?: ConnectionStatus;
    providerName?: string;
    detailsLabel?: string;
    onOpenDialog: () => void;
  };
  userProfile?: UserProfile | null;
  onAuthLogout?: () => void;
  devTools?: {
    title?: string;
    entries: Array<{
      label: string;
      value: string;
      tone?: "default" | "accent" | "warn";
    }>;
    onResetState: () => void | Promise<void>;
    resetLabel?: string;
    resetting?: boolean;
  };
  /** Mobile drawer mode: controls open/close on small screens */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  uiVariant?: "desktop" | "web";
}

function getWorkspaceVisualState(workspaceTarget: Props["workspaceTarget"]): "none" | "starting" | "live" {
  if (!workspaceTarget || workspaceTarget.status !== "connected") return "none";
  const agentReady = workspaceTarget.agentStatus === "ready";
  // Agent ready is sufficient — the PTY terminal is optional (only spawned when the bottom panel opens)
  return agentReady ? "live" : "starting";
}

function workspaceIndicatorClasses(state: "none" | "starting" | "live") {
  if (state === "live") {
    return {
      container: "border border-emerald-500/20 bg-emerald-500/[0.05]",
      text: "text-emerald-400",
      label: "Connected",
      dot: "bg-emerald-400",
      pulse: true,
    };
  }

  if (state === "starting") {
    return {
      container: "border border-amber-400/20 bg-amber-400/[0.06]",
      text: "text-amber-300",
      label: "Connecting",
      dot: "bg-amber-400",
      pulse: true,
    };
  }

  return {
    container: "border border-dashed border-white/15 bg-surface",
    text: "text-stone-500",
    label: "No workspace",
    dot: "bg-stone-600",
    pulse: false,
  };
}

function groupByProject(
  sessions: ChatSession[],
): Record<string, ChatSession[]> {
  const groups: Record<string, ChatSession[]> = {};
  for (const s of sessions) {
    const folder = s.projectFolder || "Unknown";
    if (!groups[folder]) groups[folder] = [];
    groups[folder].push(s);
  }
  // Sort sessions within each group by lastMessageAt descending
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => b.lastMessageAt - a.lastMessageAt);
  }
  return groups;
}

function folderName(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

function timeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function getInitials(profile: UserProfile): string {
  const source = profile.name.trim() || profile.email.trim() || "U";
  const parts = source
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function Sidebar({
  sessions,
  activeSessionId,
  workingDirectory,
  onSelectSession,
  onDeleteSession,
  onNewChat,
  onNewProject,
  onSettingsClick,
  workspaceTarget,
  userProfile,
  onAuthLogout,
  devTools,
  mobileOpen,
  onMobileClose,
  collapsed: collapsedProp,
  onCollapsedChange,
  uiVariant = "desktop",
}: Props) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  // Support both controlled and uncontrolled modes
  const collapsed = collapsedProp ?? internalCollapsed;
  const setCollapsed = (next: boolean) => {
    if (collapsedProp === undefined) {
      setInternalCollapsed(next);
    }
    onCollapsedChange?.(next);
  };
  const [showDevTools, setShowDevTools] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // User menu is dismissed via an invisible backdrop overlay (see JSX below)
  // instead of a document-level mousedown listener, which avoids race
  // conditions with React's synthetic event system.

  // Track which folders are collapsed (hidden chat history)
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  const grouped = groupByProject(sessions);
  const workspaceState = getWorkspaceVisualState(workspaceTarget);
  const workspaceIndicator = workspaceIndicatorClasses(workspaceState);
  const displayName = userProfile?.name.trim() || userProfile?.email.trim() || "FreshHub user";
  const displayEmail = userProfile?.email.trim() || "";
  const sidebarTypeScale = uiVariant === "desktop"
    ? {
        monoTiny: "text-[0.75rem]",
        badge: "text-[0.8rem]",
        title: "text-[0.9375rem]",
        meta: "text-[0.8rem]",
        provider: "text-[0.875rem]",
      }
    : {
        monoTiny: "text-[11px]",
        badge: "text-[12px]",
        title: "text-[14px]",
        meta: "text-[12px]",
        provider: "text-[13px]",
      };

  // Current project folder first, then alphabetical
  const sortedFolders = Object.keys(grouped).sort((a, b) => {
    if (a === workingDirectory) return -1;
    if (b === workingDirectory) return 1;
    return a.localeCompare(b);
  });

  const toggleFolder = (folder: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  const renderDevToolsDialog = () => {
    if (!devTools || !showDevTools) return null;

    return (
      <div className="fixed inset-0 bg-[#021a26]/85 backdrop-blur-md flex items-center justify-center z-50">
        <div className="bg-[#021a26] rounded-lg shadow-[0_0_40px_rgba(0,0,0,0.3)] max-w-lg w-full mx-4 p-6 max-h-[85vh] overflow-y-auto border border-line-faint animate-scale-in">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-lg font-semibold text-white">
                Dev Tools
              </h2>
              <p className="font-body text-sm text-stone-500 mt-0.5">
                {devTools.title || "Local web app state"}
              </p>
            </div>
            <button
              onClick={() => setShowDevTools(false)}
              className="p-1.5 rounded-md text-stone-400 hover:text-white hover:bg-white/[0.07] transition-colors"
              aria-label="Close"
              type="button"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-2">
            {devTools.entries.map((entry) => (
              <div
                key={entry.label}
                className="rounded-lg border border-line-faint bg-surface px-4 py-3"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500">
                  {entry.label}
                </p>
                <p
                  className={`mt-1 break-words font-mono text-sm leading-relaxed ${
                    entry.tone === "accent"
                      ? "text-gold"
                      : entry.tone === "warn"
                        ? "text-amber-300"
                        : "text-stone-200"
                  }`}
                >
                  {entry.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-line-faint">
            <button
              onClick={() => {
                void devTools.onResetState();
              }}
              disabled={devTools.resetting}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/[0.08] px-3 py-2 text-sm text-stone-400 transition-all hover:border-white/[0.15] hover:text-stone-200 disabled:cursor-wait disabled:opacity-50"
              type="button"
            >
              {devTools.resetting ? (
                <span className="h-4 w-4 rounded-full border-2 border-white/10 border-t-stone-400 animate-spin" />
              ) : (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865A8.25 8.25 0 0 1 17.833 6.165l3.181 3.182"
                  />
                </svg>
              )}
              <span>{devTools.resetLabel || "Clear user state"}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (collapsed) {
    return (
      <div className="hidden md:flex relative w-12 flex-col items-center py-3 bg-surface backdrop-blur-2xl backdrop-saturate-150 border-r border-line-faint flex-shrink-0 shadow-[inset_0_0_80px_rgba(255,255,255,0.01)] overflow-hidden">
        <SidebarBlooms />
        <div className="drag-region h-8 relative" />
        <button
          onClick={() => setCollapsed(false)}
          className="no-drag relative p-2 rounded-lg text-stone-400 hover:text-gold hover:bg-navy-light transition-colors"
          title="Show sidebar"
        >
          <SidebarToggleIcon open={false} className="h-4 w-4" />
        </button>
        <GlassOrb
          tone="teal"
          size="sm"
          onClick={() => onNewChat()}
          className="no-drag mt-2"
          title="New chat"
          aria-label="New chat"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M12 5v14M5 12h14" />
          </svg>
        </GlassOrb>
        {/* Workspace button — only visible when NOT healthy (starting / disconnected) */}
        {workspaceTarget && workspaceState !== "live" && (
          <button
            onClick={workspaceTarget.onOpenDialog}
            className="no-drag relative mt-1 p-2 rounded-lg text-stone-400 hover:text-gold hover:bg-navy-light transition-colors"
            title={
              workspaceState === "starting"
                ? `Starting ${workspaceTarget.providerName || "workspace"}…`
                : "Connect workspace"
            }
          >
            {workspaceState === "starting" && (
              <span className="absolute top-1 right-1 flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-400" />
              </span>
            )}
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7.5 3.75h9A2.25 2.25 0 0 1 18.75 6v12A2.25 2.25 0 0 1 16.5 20.25h-9A2.25 2.25 0 0 1 5.25 18V6A2.25 2.25 0 0 1 7.5 3.75Zm-3 7.5h6m0 0-2.25-2.25M10.5 11.25l-2.25 2.25"
              />
            </svg>
          </button>
        )}
        <button
          onClick={onNewProject}
          className="no-drag relative mt-1 p-2 rounded-lg text-stone-400 hover:text-gold hover:bg-navy-light transition-colors"
          title="New project"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 10.5v6m3-3H9m4.06-7.19-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
            />
          </svg>
        </button>
        <div className="relative flex-1" />
        {devTools && (
          <div className="relative mt-2">
            <button
              onClick={() => setShowDevTools((prev) => !prev)}
              className={`p-2 rounded-lg transition-colors ${
                showDevTools
                  ? "bg-surface-hover text-gold"
                  : "text-stone-400 hover:text-gold hover:bg-navy-light"
              }`}
              title="Open dev tools"
              type="button"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.7}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.049.58.025 1.193-.14 1.743"
                />
              </svg>
            </button>
          </div>
        )}
        {userProfile && (
          <div className="no-drag mt-2 flex flex-col items-center gap-1">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full border border-fwf-yellow/20 bg-stone-50/5 ${sidebarTypeScale.badge} font-display font-bold tracking-[0.16em] text-gold`}
              title={displayEmail ? `${displayName}\n${displayEmail}` : displayName}
            >
              {getInitials(userProfile)}
            </div>
            {onAuthLogout && (
              <button
                onClick={onAuthLogout}
                className="p-2 rounded-lg text-stone-500 hover:text-gold hover:bg-navy-light transition-colors"
                title="Sign out"
                type="button"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        <button
          onClick={onSettingsClick}
          className="no-drag p-2 rounded-lg text-stone-400 hover:text-gold hover:bg-navy-light transition-colors"
          title="Settings"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
        </button>
      </div>
    );
  }

  // On mobile, close the drawer when a session is selected
  const handleSelectSessionMobile = (id: string) => {
    onSelectSession(id);
    onMobileClose?.();
  };

  const sidebarContent = (
    <div className={`relative w-56 flex flex-col bg-surface backdrop-blur-2xl backdrop-saturate-150 border-r border-line-faint flex-shrink-0 h-full shadow-[inset_0_0_80px_rgba(255,255,255,0.01)] overflow-hidden ${
      // On mobile, take full height inside the drawer
      mobileOpen !== undefined ? "md:h-auto" : ""
    }`}>
      <SidebarBlooms />
      {/* Drag region + header */}
      <div className="drag-region relative px-3 pt-3 pb-3">
        <div className="h-8 md:block hidden" /> {/* macOS traffic light space — desktop only */}
        <div className="no-drag flex items-center justify-between mt-1">
          {/* Wordmark only, not the lockup: the monogram and the word say the same
              thing, so pairing them in app chrome reads as two logos. */}
          <ProductMark variant="wordmark" size="sm" />
          <button
            onClick={() => {
              setCollapsed(true);
              setShowDevTools(false);
              onMobileClose?.();
            }}
            className="p-1.5 rounded-md text-stone-400 hover:text-gold hover:bg-navy-light transition-colors"
            title="Hide sidebar"
          >
            <SidebarToggleIcon open className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Creation actions */}
      <div className="relative no-drag mx-3 mb-2 space-y-0.5">
        <button
          onClick={() => onNewChat()}
          className="flex w-full items-center gap-2.5 px-1 py-1 rounded-lg transition-colors hover:bg-surface group/action"
        >
          <GlassOrb tone="teal" size="xs" decorative className="pointer-events-none flex-shrink-0">
            <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" d="M12 5v14M5 12h14" />
            </svg>
          </GlassOrb>
          <span className="font-body text-[13px] text-stone-400 group-hover/action:text-stone-200 transition-colors">New chat</span>
        </button>
        <button
          onClick={onNewProject}
          className="flex w-full items-center gap-2.5 px-1 py-1 rounded-lg transition-colors hover:bg-surface group/action"
        >
          <GlassOrb tone="neutral" size="xs" decorative className="pointer-events-none flex-shrink-0">
            <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
            </svg>
          </GlassOrb>
          <span className="font-body text-[13px] text-stone-400 group-hover/action:text-stone-200 transition-colors">New project</span>
        </button>
      </div>

      {/* Session list */}
      <div className="relative flex-1 overflow-y-auto px-2 pt-1 pb-2">
        {(() => {
          // Build the list of folders to display. If activeSessionId is null and
          // the current workingDirectory has no sessions yet, inject a virtual
          // folder so the placeholder renders under the correct project header.
          const showPlaceholder = activeSessionId === null;
          const placeholderFolder = workingDirectory || "Unknown";
          const allFolders = showPlaceholder && !grouped[placeholderFolder]
            ? [placeholderFolder, ...sortedFolders]
            : sortedFolders;
          // Dedupe in case workingDirectory is already in sortedFolders
          const uniqueFolders = [...new Set(allFolders)];

          if (uniqueFolders.length === 0 && !showPlaceholder) return null;

          return uniqueFolders.map((folder) => {
            const isFolderCollapsed = collapsedFolders.has(folder);
            return (
              <div key={folder} className="mb-3">
                {/* Folder header: clickable name + new chat button */}
                <div className="flex items-center gap-1 px-1 py-1">
                  <button
                    onClick={() => toggleFolder(folder)}
                    className="flex items-center gap-1.5 flex-1 min-w-0 px-1 py-0.5 rounded-md hover:bg-navy-light transition-colors group"
                    title={isFolderCollapsed ? "Expand folder" : "Collapse folder"}
                  >
                    {/* Chevron indicator */}
                    <svg
                      className={`w-2.5 h-2.5 text-stone-400/80 group-hover:text-stone-200 flex-shrink-0 transition-transform ${
                        isFolderCollapsed ? "" : "rotate-90"
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                    <svg
                      className="w-3.5 h-3.5 text-stone-400/80 group-hover:text-stone-200 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
                      />
                    </svg>
                    <span className={`font-mono ${sidebarTypeScale.monoTiny} font-medium text-stone-300/85 group-hover:text-stone-100 uppercase tracking-[0.12em] truncate`}>
                      {folderName(folder)}
                    </span>
                  </button>
                </div>
                {/* Session list — hidden when folder is collapsed */}
                {!isFolderCollapsed && (
                  <div className="space-y-0.5 ml-1">
                    {/* Placeholder entry for unsaved new chat in current folder */}
                    {showPlaceholder && folder === placeholderFolder && (
                      <div className="w-full text-left px-3 py-2.5 rounded-lg text-sm bg-white/[0.07] text-white">
                        <span className="font-body truncate block">
                          New chat...
                        </span>
                      </div>
                    )}
                    {(grouped[folder] || []).map((session) => (
                      <div
                        key={session.id}
                        className="group/session relative"
                      >
                        <button
                          onClick={() => handleSelectSessionMobile(session.id)}
                          className={`w-full text-left px-3 py-3 rounded-lg transition-all duration-200 ${
                            activeSessionId === session.id
                              ? "bg-white/[0.07] text-white"
                              : "text-stone-400 hover:bg-surface-hover"
                          }`}
                        >
                          <span className={`font-body ${sidebarTypeScale.title} font-medium truncate block pr-5 leading-tight`}>
                            {session.title}
                          </span>
                          <span className={`mt-1 flex items-center gap-1.5 font-mono ${sidebarTypeScale.monoTiny} text-stone-500`}>
                            <span>{timeAgo(session.lastMessageAt)}</span>
                            <span className="inline-block h-0.5 w-0.5 rounded-full bg-stone-600" />
                            <span>{session.messageCount} msg{session.messageCount !== 1 ? "s" : ""}</span>
                          </span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteSession(session.id);
                          }}
                          className="absolute right-1 top-1.5 p-0.5 rounded opacity-0 group-hover/session:opacity-100 text-stone-500 hover:text-red-400 hover:bg-navy-light transition-all"
                          title="Delete conversation"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18 18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>

      {/* Footer */}
      <div className="relative no-drag border-t border-line-faint px-2 py-2 space-y-0.5">
        {workspaceTarget && (
          <button
            onClick={workspaceTarget.onOpenDialog}
            className="group flex w-full items-center gap-2 rounded-lg px-2 h-[34px] text-stone-400 hover:text-gold hover:bg-surface-hover transition-colors"
            type="button"
            title={`${workspaceIndicator.label}${workspaceTarget.detailsLabel ? ` · ${workspaceTarget.detailsLabel}` : ""}`}
          >
            {workspaceState === "starting" ? (
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${workspaceIndicator.dot} opacity-75`} />
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${workspaceIndicator.dot}`} />
              </span>
            ) : (
              <span className={`inline-flex rounded-full h-1.5 w-1.5 ${workspaceIndicator.dot} flex-shrink-0`} />
            )}
            <span className={`truncate font-body ${sidebarTypeScale.meta} text-stone-400 group-hover:text-gold transition-colors`}>
              {workspaceTarget.providerName || "Workspace"}
            </span>
          </button>
        )}
        {/* User profile row + popover menu */}
        {userProfile && (
          <div className="relative">
            {/* Popover menu */}
            {showUserMenu && (
              <>
              {/* Invisible backdrop to catch outside clicks */}
              <div className="fixed inset-0 z-40" onMouseDown={() => setShowUserMenu(false)} />
              <div
                className="absolute bottom-full left-0 right-0 mb-1.5 rounded-xl border border-white/[0.08] bg-[#081c26] p-1 shadow-[0_16px_48px_rgba(0,0,0,0.5)] animate-scale-in z-50">
                {displayEmail && (
                  <div className={`px-3 py-2 font-body ${sidebarTypeScale.meta} text-stone-500 truncate`}>
                    {displayEmail}
                  </div>
                )}
                <button
                  onClick={() => { setShowUserMenu(false); onSettingsClick(); }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-stone-300 hover:text-gold hover:bg-surface-hover transition-colors"
                  type="button"
                >
                  <svg className="w-3.5 h-3.5 text-stone-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                  <span className={`font-body ${sidebarTypeScale.meta}`}>Settings</span>
                </button>
                {devTools && (
                  <button
                    onClick={() => { setShowUserMenu(false); setShowDevTools(true); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-stone-300 hover:text-gold hover:bg-surface-hover transition-colors"
                    type="button"
                  >
                    <svg className="w-3.5 h-3.5 text-stone-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.049.58.025 1.193-.14 1.743" />
                    </svg>
                    <span className={`font-body ${sidebarTypeScale.meta}`}>Dev Tools</span>
                  </button>
                )}
                {onAuthLogout && (
                  <>
                    <div className="my-1 border-t border-line-faint" />
                    <button
                      onClick={() => { setShowUserMenu(false); onAuthLogout(); }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-stone-400 hover:text-red-400 hover:bg-surface-hover transition-colors"
                      type="button"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                      </svg>
                      <span className={`font-body ${sidebarTypeScale.meta}`}>Sign out</span>
                    </button>
                  </>
                )}
              </div>
              </>
            )}
            {/* User row trigger */}
            <button
              onClick={() => setShowUserMenu((prev) => !prev)}
              className={`group flex w-full items-center gap-2 rounded-lg px-2 h-[36px] transition-colors ${
                showUserMenu ? "bg-surface-hover" : "hover:bg-surface-hover"
              }`}
              type="button"
            >
              <div className={`flex h-6 w-6 items-center justify-center rounded-full bg-surface-hover ${sidebarTypeScale.monoTiny} font-display font-bold tracking-[0.16em] text-gold flex-shrink-0`}>
                {getInitials(userProfile)}
              </div>
              <div className={`truncate font-body ${sidebarTypeScale.meta} text-stone-300 min-w-0 flex-1 text-left`}>
                {displayName}
              </div>
              <svg className={`w-3 h-3 text-stone-500 flex-shrink-0 transition-transform ${showUserMenu ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // On desktop (md+), render sidebar inline. On mobile, render as a drawer overlay.
  return (
    <>
      {/* Desktop: always visible inline */}
      <div className="hidden md:flex h-full flex-shrink-0">
        {sidebarContent}
      </div>

      {/* Mobile: drawer overlay */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-opacity duration-200 ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60"
          onClick={onMobileClose}
        />
        {/* Drawer panel */}
        <div
          className={`absolute inset-y-0 left-0 transition-transform duration-200 ease-out ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {sidebarContent}
        </div>
      </div>

      {/* Dev Tools dialog — rendered outside sidebar containers to avoid overflow clip */}
      {renderDevToolsDialog()}
    </>
  );
}
